<template>
    <v-row
        no-gutters
        class="mb-4"
    >
        <v-col>
            <v-dialog
                v-if="calendar_dialog.open"
                v-model="calendar_dialog.open"
                :width="700"
            >
                <AdminCalendarDialog
                    :event-id="calendar_dialog.id"
                    @close="onCloseEdit"
                ></AdminCalendarDialog>
            </v-dialog>

            <v-row no-gutters>
                <v-col class="mb-2">
                    <NewFilterBar
                        :available-filters="filterHelper.getAvailableFilters()"
                        :before-route-change="filterHelper.setFilterParamsIfEmpty.bind(filterHelper)"
                        @route_change="debouncedBackendQuery"
                    >
                        <template v-slot:right="{filters, filterAdd}">
                            <AddFilterButton
                                :filters="filters"
                                @add_filter="filterAdd($event)"
                            />
                            <v-btn
                                icon
                                color="n3"
                                class="tooltip-bottom text-none"
                                :disabled="Object.keys($route.query).length < 1"
                            >
                                <v-icon>mdi-content-save</v-icon>
                                <span class="tooltiptext">{{ $t("Save filters") }}</span>
                            </v-btn>
                        </template>
                    </NewFilterBar>
                </v-col>
            </v-row>
            <v-row no-gutters>
                <v-col>
                    <v-toolbar
                        flat
                        class="medium-toolbar"
                        color="transparent"
                    >
                        <v-toolbar-title class="font-weight-bold mb-3">
                            {{ total_count }}
                            {{ $tc("event | events", total_count) }}
                        </v-toolbar-title>
                    </v-toolbar>
                </v-col>
                <v-col class="text-right">
                    <v-btn
                        depressed
                        color="primary"
                        class="inverted--text"
                        @click="onOpenEdit(0)"
                    >
                        <v-icon>add</v-icon>
                        {{ $t("Add event") }}
                    </v-btn>
                </v-col>
            </v-row>
            <v-row no-gutters>
                <v-col>
                    <v-data-table
                        :headers="headers"
                        :items="items"
                        :options="filterHelper.getTableOptions()"
                        :server-items-length="total_count"
                        :footer-props="table_footer_props"
                        hide-default-header
                        class="elevation-2 medium-dense-table"
                        @update:options="filterHelper.setTableOptions($event)"
                    >
                        <template v-slot:header="{ props, on }">
                            <thead class="v-data-table-header">
                            <tr>
                                <th
                                    v-for="(header, index) in props['headers']"
                                    :key="index"
                                    :class="[formatTableHeaderClass(header, props['options'])]"
                                    :width="header.width"
                                    role="columnheader"
                                    scope="col"
                                    v-on="on"
                                    @click="
                                            header.sortable !== false && header['value'] !== 'data-table-select'
                                                ? filterHelper.changeSort(header.value)
                                                : ''
                                        "
                                >
                                        <span class="subtitle-2">
                                            {{ header.text }}
                                        </span>
                                    <v-icon v-if="header.value === filterHelper.getUrlParam('sortBy')">
                                        {{ filterHelper.getUrlParam('sortDesc') ? "mdi-menu-down" : "mdi-menu-up" }}
                                    </v-icon>
                                </th>
                            </tr>
                            </thead>
                        </template>

                        <template v-slot:item.date_from="{ item }">
                            {{ formatDatesFromUntil(parseSysDate(item.date_from), parseSysDate(item.date_until)) }}
                        </template>
                        <template v-slot:item.country_code="{ item }">
                            {{ countryName(item.country_code) }}
                        </template>
                        <template v-slot:item.type="{ item }">
                            {{ typeName(item.type) }}
                        </template>
                        <template v-slot:item.edit="{ item }">
                            <div class="text-right">
                                <v-btn
                                    icon
                                    text
                                    color="n3"
                                    @click="onOpenEdit(item.id)"
                                >
                                    <v-icon>edit</v-icon>
                                </v-btn>
                                <v-btn icon color="n3">
                                    <v-icon @click="onDeleteCalendarEvent(item)">delete</v-icon>
                                </v-btn>
                            </div>
                        </template>
                    </v-data-table>
                </v-col>
            </v-row>
        </v-col>
    </v-row>
</template>

<script>
import Vue from "vue";
import AppModule from "../../store/modules/app";
import NewFilterBar from "../../components/NewFilterBar";
import AddFilterButton from "@/components/NewFilterBar/components/AddFilterButton";
import {getFilterHelper} from "@/components/NewFilterBar/FilterBar";
import {$t} from "@/i18n";
import {computed, reactive, ref, toRefs} from "@vue/composition-api";
import CalendarEventModule from "../../store/modules/calendar_event";
import {useTable} from "@/composables/use-table";
import AdminCalendarDialog from "./AdminCalendarDialog.vue";
import AccountModule from "../../store/modules/account";
import DialogModule from "../../store/modules/dialog";
import {debounce} from "throttle-debounce";
import errorDialog from "@/views/ErrorDialog/errorDialog"
import {formatDatesFromUntil, parseSysDate} from "@/plugins/formatting";

export default {
    name: "AdminCalendar",
    components: {NewFilterBar, AdminCalendarDialog, AddFilterButton},
    setup() {
        const calendarEventModule = CalendarEventModule();
        const {openConfirmDialog, setConfirmOptions, openDialog, setDialogOptions} = DialogModule();
        const appModule = AppModule();
        const {table_footer_props, formatTableHeaderClass} = useTable(["date"], true);
        const items = ref([]);
        const total_count = ref(0);

        const state = reactive({
            namespace: "admin_calendar",
            headers: [
                {text: $t("Country"), value: "country_code"},
                {text: $t("Type"), value: "type"},
                {text: $t("Date"), value: "date_from"},
                {text: $t("Name"), value: "name"},
                {text: $t("Link"), value: "link", sortable: false},
                {text: "", value: "edit", sortable: false}
            ],
            profile_params: {
                name: "name",
                classes: "text-xs-left",
                clickable: false
            },
            role: "",
            email: ""
        });

        const calendar_dialog = reactive({
            open: false,
            id: 0
        });

        const countries = computed(function () {
            return appModule.countries.value;
        });

        const debouncedBackendQuery = debounce(300, false, () => {
            backendQuery();
        });

        const filterHelper = getFilterHelper({
            filters: [
                {
                    key: "dates_list",
                    value: [
                        new Date().calcMonthStart().formatSysDate(),
                        new Date().calcMonthEnd().formatSysDate(),
                        "som_0",
                        "eom_0"
                    ],
                    default: true
                },
                {key: "country_code", value: "", items: appModule.countries, default: true},
                {key: "area_id", value: "", items: appModule.business_areas, default: true},
            ]
        })
        filterHelper.setFilterParamsIfEmpty()

        const types = calendarEventModule.types.value;
        const typeName = function (type_code) {
            var item = types.find(function (item) {
                return item.code === type_code ? item : "";
            });
            if (typeof item == "undefined") {
                return "";
            }
            return item.name;
        };
        const {current_account} = AccountModule();

        const countryName = function (countryCode) {
            if (typeof countries.value == "undefined") {
                return "";
            }
            let item = countries.value.find(function (country) {
                return countryCode && countryCode.toLowerCase() === country.code.toLowerCase() ? country : "";
            });
            if (typeof item == "undefined") {
                return "";
            }
            return item.name;
        };
        const load_data = function () {
            appModule
                .fetchClassifiers({
                    types: ["countries", "business_areas"],
                    account_id: current_account.value.id
                })
                .catch(function (ex) {
                    errorDialog.showError(ex);
                });
        };

        const onOpenEdit = function (id) {
            calendar_dialog.id = id;
            calendar_dialog.open = true;
        };
        const onCloseEdit = function (data) {
            if (typeof data !== "undefined" && data.event_id > 0) {
                const found = items.value.find(({id}) => id === data.event_id);
                if (found) {
                    Vue.sync_item_changes(found, data);
                }
            } else {
                backendQuery();
            }

            calendar_dialog.open = false;
            calendar_dialog.id = null;
        };

        const backendQuery = () => {
            calendarEventModule.fetchCalendarEventList(filterHelper.getOnlyRelevantQueryParams())
                .then(function (resp) {
                    items.value = resp.items;
                    total_count.value = resp.total_count;
                })
                .catch(function (ex) {
                    errorDialog.showError(ex);
                });
        };

        const isDeleteConfirmed = function (event) {
            calendarEventModule.deleteAdminCalendarEvent(event.id)
                .then(function () {
                    for (let i in items.value) {
                        if (items.value[i].id !== event.id) continue;
                        items.value.splice(i, 1);
                        break;
                    }
                })
                .catch(function (ex) {
                    errorDialog.showError(ex);
                });
        };

        const onDeleteCalendarEvent = function (event) {
            setConfirmOptions({
                title: $t("Are you sure you wish to delete this event?"),
                confirm_text: $t("Delete"),
                cancel_text: $t("Cancel"),
                action: () => isDeleteConfirmed(event)
            });
            openConfirmDialog();
        };

        const onSaveFiltersClick = () => {
            setDialogOptions({
                component: () => import("@/views/Dialog/BookmarkDialog"),
                payload: {
                    type: "admin"
                }
            });
            openDialog();
        };

        load_data();

        return {
            ...toRefs(state),
            debouncedBackendQuery,
            table_footer_props,
            formatTableHeaderClass,
            items,
            total_count,
            calendar_dialog,
            onOpenEdit,
            onCloseEdit,
            types,
            typeName,
            countryName,
            onDeleteCalendarEvent,
            onSaveFiltersClick,
            filterHelper,
            formatDatesFromUntil,
            parseSysDate
        };
    }
};
</script>
